import BaseListPresenter from "../../../base/BaseListPresenter";
import DeleteModal from "../../../components/common/DeleteModal";
import { dialog } from "nq-component";
import * as XLSX from "xlsx";
import {
  loadOrganizationNameMaps,
  resolveOrganizationFields,
} from "../../HumanResource/Reports/organizationExportUtils";

/** Matches Hierarchy.js labels for org structure columns. */
const DEPARTMENT_HEAD_EXPORT_COLUMNS = [
  { field: "region", header: "OCOM" },
  { field: "office", header: "Location" },
  { field: "division", header: "Region" },
  { field: "section", header: "Subport/ IPEE/ Main RIOU" },
  { field: "unit", header: "Division" },
  { field: "cluster", header: "Section/ Unit/ Other Offices" },
  { field: "supervisor", header: "Supervisor" },
];

function formatSupervisorName(supervisor) {
  if (!supervisor || typeof supervisor !== "object") return "";
  return `${
    supervisor.surname || ""
  }, ${supervisor.Firstname || ""} ${supervisor.Middlename || ""}`.trim();
}

class DepartmentHeadPagePresenter extends BaseListPresenter {
  constructor(
    view,
    findObjectUseCase,
    countObjectUseCase,
    deleteObjectUseCase,
    upsertUseCase,
    exportCSVUseCase,
    addSchemaUseCase,
    updateSchemaUseCase,
    deleteSchemaUseCase,
  ) {
    super(view, findObjectUseCase, countObjectUseCase, deleteObjectUseCase);
    this.upsertUseCase = upsertUseCase;
    this.exportCSVUseCase = exportCSVUseCase;
    this.addSchemaUseCase = addSchemaUseCase;
    this.updateSchemaUseCase = updateSchemaUseCase;
    this.deleteSchemaUseCase = deleteSchemaUseCase;
    this.view = view;
  }
  init() {
    this.limit = 10;
    this.where = {};
    this.search = {};
    this.filter = {};
    this.include = ["all"];
    this.keys = undefined; // if keys are specified, only those keys will be returned
    this.sort = { created: -1 };
    this.progress = true;
    this.reset();
  }

  createQuery() {
    const skip = (this.current - 1) * this.limit;
    const query = {
      limit: this.limit,
      skip: skip,
      where: { ...this.where, ...this.search, ...this.filter },
      include: this.include,
    };
    if (this.sort) {
      query.sort = this.sort;
    }
    const keys = this.keys || this.view.getKeys() || [];
    if (keys.length > 0) {
      query.keys = keys;
    }
    return query;
  }

  async findObjects() {
    const collection = this.view.getCollectionName();
    const query = this.createQuery();
    const [regions, offices, divisions, sections, units, clusters] =
      await Promise.all([
        this.findObjectUseCase.execute("Region", {
          where: { status: "active" },
          keys: ["name"],
        }),
        this.findObjectUseCase.execute("offices", {
          where: { status: "active" },
          keys: ["name"],
        }),
        this.findObjectUseCase.execute("Division", {
          where: { status: "active" },
          keys: ["name"],
        }),
        this.findObjectUseCase.execute("Sections", {
          where: { status: "active" },
          keys: ["name"],
        }),
        this.findObjectUseCase.execute("Units", {
          where: { status: "active" },
          keys: ["name"],
        }),
        this.findObjectUseCase.execute("Clusters", {
          where: { status: "active" },
          keys: ["name"],
        }),
      ]);
    try {
      this.showProgress();
      this.findObjectUseCase.abort();
      const objects = await this.findObjectUseCase.execute(collection, query);
      objects.forEach((obj) => {
        ["region", "office", "division", "section", "unit", "cluster"].forEach(
          (field) => {
            if (obj[field]) {
              const collectionMap = {
                region: regions,
                office: offices,
                division: divisions,
                section: sections,
                unit: units,
                cluster: clusters,
              };

              const findObj = collectionMap[field]?.find(
                (r) => r.id === obj[field],
              );

              obj[field] = findObj?.name || "";
            }
          },
        );
      });

      this.objects = this.objects.concat(objects);
      this.view.setObjects(objects);
      this.view.setCurrent(this.current);
      this.hideProgress();
    } catch (error) {
      this.hideProgress();
      this.view.showError(error);
    }
    this.progress = false;
  }

  handleHierarchyChange = (hierarchy) => {
    console.log("currentFilters: ", hierarchy);
    const currentFilters = { ...this.view.state.filters };
    console.log("currentFilters: ", currentFilters);
    const formatter = {
      region: "regions",
      office: "offices",
      division: "divisions",
      unit: "units",
      cluster: "plantilla",
    };

    const hierarchyOrder = ["region", "office", "division", "unit", "cluster"];

    Object.entries(hierarchy).forEach(([key, value]) => {
      const mappedKey = formatter[key] ?? key;
      if (value) {
        currentFilters[mappedKey] = value;
      } else {
        delete currentFilters[mappedKey];
      }
    });

    // Get the last selected key in hierarchy order
    const lastSelectedKey = hierarchyOrder
      .filter((key) => hierarchy[key])
      .pop();

    // Build filter using only the last selected
    const applyFilter = lastSelectedKey
      ? { [formatter[lastSelectedKey]]: hierarchy[lastSelectedKey] }
      : {};

    this.view.setState({ applyFilter });

    this.view.setState(
      (prevState) => {
        const updatedFilters = { ...prevState.filters };

        // Explicitly remove keys that are now empty/null
        Object.entries(hierarchy).forEach(([key, value]) => {
          if (!value) {
            delete updatedFilters[key]; // Remove cleared keys
          } else {
            updatedFilters[key] = value; // Add/update active keys
          }
        });

        return { filters: updatedFilters };
      },
      () => {
        this.view.filterSubmit(this.view.state.filters);
      },
    );
  };

  setCurrentPage(page) {
    this.current = page;
    this.documents = [];
    this.view.setCurrent(page);
    this.view.setObjects([]);
    this.findObjects();
  }

  handleDeleteData(data) {
    DeleteModal(async () => {
      if (this._deleting) return;
      this._deleting = true;
      dialog.close();
      this.view.showProgressDialog();
      try {
        await this.deleteObjectUseCase.execute(
          this.view.getCollectionName(),
          data.id,
        );
        await this.view.refetchData();
        this.view.hideProgressDialog();
        this.view.showSuccess("Record Deleted Succesfully!");
      } catch (error) {
        this.view.hideProgressDialog();
        this.view.showError(error?.message || error);
      } finally {
        this._deleting = false;
      }
    });
  }

  async exportToExcel() {
    if (this._exporting) return;
    this._exporting = true;
    this.view.setState({ exporting: true });

    const collection = this.view.getCollectionName();
    const where = { ...this.where, ...this.search, ...this.filter };
    const PAGE = 500;
    let skip = 0;
    const all = [];

    try {
      this.view.showProgressDialog();
      while (true) {
        const batch = await this.findObjectUseCase.execute(collection, {
          where,
          include: ["selectedSupervisor"],
          keys: [
            "region",
            "office",
            "division",
            "section",
            "unit",
            "cluster",
            "selectedSupervisor",
          ],
          limit: PAGE,
          skip,
          ...(this.sort ? { sort: this.sort } : {}),
        });
        if (!batch?.length) break;
        all.push(...batch);
        if (batch.length < PAGE) break;
        skip += PAGE;
      }

      const maps = await loadOrganizationNameMaps(this.findObjectUseCase);
      resolveOrganizationFields(all, maps);

      const rows = all.map((obj) => {
        const row = {};
        DEPARTMENT_HEAD_EXPORT_COLUMNS.forEach(({ field, header }) => {
          if (field === "supervisor") {
            row[header] = formatSupervisorName(obj.selectedSupervisor);
          } else {
            row[header] = obj[field] != null ? String(obj[field]) : "";
          }
        });
        return row;
      });

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(rows);
      const headers = DEPARTMENT_HEAD_EXPORT_COLUMNS.map((c) => c.header);
      worksheet["!cols"] = headers.map((header) => {
        const maxWidth = Math.max(
          header.length,
          ...rows.map((r) => (r[header] || "").length),
        );
        return { wch: Math.min(Math.max(maxWidth + 2, 10), 50) };
      });

      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (worksheet[cellAddress]) {
          worksheet[cellAddress].s = {
            font: { bold: true },
            alignment: { vertical: "center", horizontal: "center" },
          };
        }
      }

      XLSX.utils.book_append_sheet(workbook, worksheet, "Department Head");
      const label =
        this.view.getSchema(collection)?.label || "Department Head";
      XLSX.writeFile(workbook, `${label}.xlsx`, { cellStyles: true });
      // Close busy before success — nq dialog stacks nodes.
      this.view.hideProgressDialog();
      this.view.showSuccess("Export completed");
    } catch (error) {
      this.view.hideProgressDialog();
      this.view.showError(error?.message || error);
    } finally {
      this._exporting = false;
      this.view.setState({ exporting: false });
    }
  }
}
export default DepartmentHeadPagePresenter;
